import { CSSProperties, useEffect, useRef, useState } from 'react';
import { Row, Typography } from '@tempmee/react';
import { SolidStarIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Popover } from '~/commonComponents/Popover/Popover';
import { RatingContent } from './RatingContent';
import { IProfessionalCardRating } from '../../interfaces';

// ── inline styles (no DS prop equivalent) ──────────────────────────────────
/** underline has no DS Typography prop */
const ratingValueStyle: CSSProperties = { textDecoration: 'underline' };
/** underline + dimmed when the professional is unavailable (dynamic opacity has no DS prop) */
const getRatingValueStyle = (isAvailable: boolean): CSSProperties => ({
  ...ratingValueStyle,
  opacity: isAvailable ? 1 : 0.5,
});

interface IProfessionalRatingProps {
  rating?: IProfessionalCardRating;
  isAvailable?: boolean;
}

export const ProfessionalRating = ({ rating, isAvailable = true }: IProfessionalRatingProps) => {
  const targetRef = useRef<HTMLElement | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<'top' | 'bottom'>('bottom');

  const showRatingBreakdown =
    rating?.professionalSkills != null ||
    rating?.interpersonalSkills != null ||
    rating?.timeManagement != null;

  const updatePopoverPosition = () => {
    if (!targetRef.current) return;

    const rect = targetRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const buffer = 250; // adjust as needed for popover height

    if (viewportHeight - rect.bottom < buffer) {
      setPopoverPosition('top');
    } else {
      setPopoverPosition('bottom');
    }
  };

  useEffect(() => {
    updatePopoverPosition();

    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition);
    };
  }, []);

  if (rating == null) {
    return null;
  }

  return (
    <Row align="center" gap={1}>
      {/* dynamic opacity for the unavailable state has no DS prop; kept inline */}
      <SolidStarIcon
        size={20}
        color={colors.success['400']}
        style={{ opacity: isAvailable ? 1 : 0.5 }}
      />

      {showRatingBreakdown ? (
        <Popover
          position={popoverPosition}
          trigger="hover"
          Target={() => (
            <Typography
              ref={targetRef}
              as="span"
              bold
              size="heading_4"
              color={colors.neutral['600']}
              style={getRatingValueStyle(isAvailable)}
            >
              {rating?.averageRating?.toFixed(1)}
            </Typography>
          )}
          Content={() => (
            <RatingContent
              professionalSkills={rating.professionalSkills ?? 5}
              interpersonalSkills={rating.interpersonalSkills ?? 5}
              timeManagement={rating.timeManagement ?? 5}
            />
          )}
        />
      ) : (
        <Typography
          as="span"
          bold
          size="heading_4"
          color={colors.neutral['600']}
          style={ratingValueStyle}
        >
          {rating?.averageRating?.toFixed(1)}
        </Typography>
      )}
    </Row>
  );
};
